import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { CheckCircle2, AlertTriangle, ExternalLink, RefreshCw } from "lucide-react";
import { api, formatApiError } from "../lib/api";

/**
 * Post-onboarding Stripe Connect status card. Surfaces:
 *  - charges_enabled (green) → ready to receive
 *  - details_submitted but not charges_enabled (amber) → Stripe is reviewing
 *  - details_submitted=false (red) → user abandoned onboarding
 *
 * Buttons:
 *  - "Open Stripe dashboard" → one-time login link to the Express dashboard
 *  - "Resume onboarding" → re-mints AccountLink if still incomplete
 */
export default function StripeConnectCard() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/payments/connect/status");
      setStatus(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't load Stripe status");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const openDashboard = async () => {
    try {
      const { data } = await api.post("/payments/connect/dashboard-link");
      window.open(data.url, "_blank", "noopener,noreferrer");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
  };

  const resumeOnboarding = async () => {
    try {
      const { data } = await api.post("/payments/connect/onboard", { origin_url: window.location.origin });
      window.open(data.onboarding_url, "_blank", "noopener,noreferrer");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
  };

  if (loading || !status) {
    return (
      <div className="mt-4 pp-card p-4 text-xs text-zinc-500" data-testid="stripe-status-loading">
        Loading Stripe status…
      </div>
    );
  }

  const ready = status.charges_enabled;
  const submitted = status.details_submitted;
  const demo = status.demo_mode;

  let bucket;
  if (demo && ready) bucket = { color: "#FFD60A", icon: AlertTriangle, label: "Demo mode", desc: "Real Stripe Connect requires a platform STRIPE_API_KEY. Payouts are simulated for now." };
  else if (ready) bucket = { color: "#00E676", icon: CheckCircle2, label: "Connected", desc: "You can receive payments." };
  else if (submitted) bucket = { color: "#FFD60A", icon: AlertTriangle, label: "Stripe is reviewing", desc: "Most accounts activate within minutes. Check back soon." };
  else bucket = { color: "#FF3B30", icon: AlertTriangle, label: "Onboarding incomplete", desc: "Finish onboarding to start receiving payments." };

  const Icon = bucket.icon;

  return (
    <section className="mt-4 pp-card p-4" data-testid="stripe-connect-card">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-lg flex items-center justify-center" style={{ background: `${bucket.color}1A`, border: `1px solid ${bucket.color}40` }}>
            <Icon size={20} style={{ color: bucket.color }} />
          </div>
          <div>
            <div className="font-bold flex items-center gap-2">
              Stripe payouts <span className="text-xs px-2 py-0.5 rounded-full" style={{ background: `${bucket.color}1A`, color: bucket.color }}>{bucket.label}</span>
            </div>
            <div className="text-xs text-zinc-500 mt-0.5">{bucket.desc}</div>
          </div>
        </div>
        <div className="flex gap-2">
          {ready && !demo && (
            <button onClick={openDashboard} data-testid="stripe-dashboard-btn" className="pp-btn-secondary px-3 py-2 rounded-lg text-xs flex items-center gap-1">
              <ExternalLink size={12} /> Stripe dashboard
            </button>
          )}
          {!ready && !demo && (
            <button onClick={resumeOnboarding} data-testid="stripe-resume-btn" className="pp-btn-primary px-3 py-2 rounded-lg text-xs flex items-center gap-1">
              <ExternalLink size={12} /> Resume onboarding
            </button>
          )}
          <button onClick={refresh} title="Refresh status" data-testid="stripe-refresh-btn" className="pp-btn-secondary p-2 rounded-lg">
            <RefreshCw size={12} />
          </button>
        </div>
      </div>

      {!ready && (status.requirements_currently_due?.length || 0) > 0 && (
        <div className="mt-3 pt-3 border-t border-white/5" data-testid="stripe-requirements">
          <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1.5">Stripe still needs</div>
          <div className="flex flex-wrap gap-1.5">
            {status.requirements_currently_due.slice(0, 6).map((r) => (
              <span key={r} className="text-[10px] px-2 py-0.5 rounded-full bg-white/[0.04] border border-white/10 text-zinc-400">
                {r.replace(/_/g, " ").replace(/\./g, " · ")}
              </span>
            ))}
          </div>
        </div>
      )}
    </section>
  );
}
